import { isHttpClientError } from "../../../../lib/http/errors";
import { defaultSortDirection, defaultSortField, mobileBreakpoint, tabletBreakpoint } from "../config";
import type {
  MySqlQueryListItem,
  MySqlQueryListParams,
  MySqlQueryRecordFormValues,
  MySqlQueryRecordMutation,
  PaginationItem,
  QueryFormState,
  ViewportMode,
} from "../types";

/** 列表请求状态。 */
export type MySqlQueryListRequestState = {
  page: number;
  pageSize: number;
  filters: QueryFormState;
};

/** 列表查询根 key。 */
export const mySqlQueryListQueryRootKey = ["demo", "mysql-query-list"] as const;

/** 根据窗口宽度判断当前视口模式。 */
export function getViewportMode(width: number): ViewportMode {
  if (width < mobileBreakpoint) {
    return "mobile";
  }
  if (width < tabletBreakpoint) {
    return "tablet";
  }
  return "desktop";
}

/** 构建分页按钮序列，移动端最多展示 5 个页码。 */
export function buildPaginationItems(
  currentPage: number,
  totalPages: number,
  isMobile: boolean,
): PaginationItem[] {
  if (totalPages <= (isMobile ? 5 : 7)) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  if (isMobile) {
    if (currentPage <= 3) {
      return [1, 2, 3, 4, 5];
    }

    if (currentPage >= totalPages - 2) {
      return Array.from({ length: 5 }, (_, index) => totalPages - 4 + index);
    }

    return [currentPage - 2, currentPage - 1, currentPage, currentPage + 1, currentPage + 2];
  }

  if (currentPage <= 4) {
    return [1, 2, 3, 4, 5, "ellipsis-right", totalPages];
  }

  if (currentPage >= totalPages - 3) {
    return [1, "ellipsis-left", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  }

  return [1, "ellipsis-left", currentPage - 1, currentPage, currentPage + 1, "ellipsis-right", totalPages];
}

/** 构建列表查询参数。 */
export function buildMySqlQueryListParams(state: MySqlQueryListRequestState): MySqlQueryListParams {
  const { filters, page, pageSize } = state;

  return {
    page,
    pageSize,
    recordName: filters.recordName.trim() || undefined,
    source: filters.source || undefined,
    tags: filters.tags.length > 0 ? filters.tags : undefined,
    bizDate: filters.bizDate || undefined,
    createdFrom: normalizeStartDateValue(filters.createdRange[0]),
    createdTo: normalizeEndDateValue(filters.createdRange[1]),
    enabled: filters.enabledOnly,
    sortField: defaultSortField,
    sortDirection: defaultSortDirection,
  };
}

/** 构建列表查询 key。 */
export function buildMySqlQueryListQueryKey(
  state: MySqlQueryListRequestState,
  queryRootKey: readonly string[] = mySqlQueryListQueryRootKey,
) {
  return [...queryRootKey, buildMySqlQueryListParams(state)] as const;
}

/** 统一格式化金额显示。 */
export function formatAmount(value: unknown): string {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value.toFixed(2) : "--";
  }

  if (typeof value === "string") {
    const normalized = Number(value);
    return Number.isFinite(normalized) ? normalized.toFixed(2) : value;
  }

  return "--";
}

/** 将列表项映射为弹窗表单初始值。 */
export function mapRecordToFormValues(item: MySqlQueryListItem): MySqlQueryRecordFormValues {
  return {
    recordName: item.recordName,
    source: item.source,
    tags: item.tags,
    bizDate: item.bizDate,
    amount: item.amount,
    enabled: item.enabled,
  };
}

/** 将弹窗表单值映射为 API 请求载荷。 */
export function mapRecordFormValuesToPayload(
  values: MySqlQueryRecordFormValues,
): MySqlQueryRecordMutation {
  return {
    recordName: values.recordName.trim(),
    source: values.source,
    tags: values.tags,
    bizDate: values.bizDate,
    amount: Number(values.amount ?? 0),
    enabled: values.enabled,
  };
}

/** 将开始日期转换为当天开始时间。 */
export function normalizeStartDateValue(value: string): string | undefined {
  if (!value) {
    return undefined;
  }
  return `${value} 00:00:00`;
}

/** 将结束日期转换为当天结束时间。 */
export function normalizeEndDateValue(value: string): string | undefined {
  if (!value) {
    return undefined;
  }
  return `${value} 23:59:59`;
}

/** 判断删除后是否需要回退到上一页。 */
export function getDeleteFallbackPage(page: number, currentPageItemCount: number): number {
  if (page > 1 && currentPageItemCount === 1) {
    return page - 1;
  }
  return page;
}

/** 返回筛选表单的深拷贝，避免引用共享带来状态串扰。 */
export function cloneFormState(formState: QueryFormState): QueryFormState {
  return {
    recordName: formState.recordName,
    source: formState.source,
    tags: [...formState.tags],
    bizDate: formState.bizDate,
    createdRange: [...formState.createdRange],
    enabledOnly: formState.enabledOnly,
  };
}

/** 比较两份筛选表单是否一致。 */
export function isSameFormState(left: QueryFormState, right: QueryFormState): boolean {
  if (
    left.recordName !== right.recordName ||
    left.source !== right.source ||
    left.bizDate !== right.bizDate ||
    left.enabledOnly !== right.enabledOnly
  ) {
    return false;
  }

  if (left.tags.length !== right.tags.length || left.createdRange.length !== right.createdRange.length) {
    return false;
  }

  return (
    left.tags.every((value, index) => value === right.tags[index]) &&
    left.createdRange.every((value, index) => value === right.createdRange[index])
  );
}

/** 将删除失败原因转换为用户可理解的中文提示。 */
export function getDeleteErrorMessage(error: unknown): string {
  if (isHttpClientError(error)) {
    if (error.code === "MYSQL_QUERY_RECORD_SOFT_DELETE_UPGRADE_REQUIRED") {
      return "当前环境尚未完成软删除字段升级，删除功能暂不可用。请先执行 docs/sql/2026-04-08-demo-query-record-crud.sql。";
    }

    if (error.code === "MYSQL_QUERY_RECORD_ALREADY_DELETED") {
      return "该记录已删除，请刷新列表后重试。";
    }

    if (error.code === "MYSQL_QUERY_RECORD_NOT_FOUND") {
      return "未找到要删除的记录，请刷新列表后重试。";
    }
  }

  return error instanceof Error ? error.message : "删除失败，请稍后重试。";
}
